import { state as animationState, style, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  CitySelectorComponent,
  CitySelectorType,
} from '@dynamic-components/booker-v2/shared/city-selector';
import { PairedRecentAirportSearches } from '@store/booker/types/booker-selector.types';
import { Airport, CountryWithAirports } from '@store/origins/types';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, filter, skip, take } from 'rxjs/operators';

import { CityPairSelector } from './types/city-pair-selector.type';

export interface CityPair {
  origin: Airport;
  destination: Airport;
}

@Component({
  selector: 'dot-city-pair-selector',
  templateUrl: './city-pair-selector.component.html',
  styleUrls: ['./city-pair-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CityPairSelectorComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: CityPairSelectorComponent,
      multi: true,
    },
  ],
  animations: [
    trigger('swapIcon', [
      animationState('false', style({ transform: 'rotate(0deg)' })),
      animationState('true', style({ transform: 'rotate(180deg)' })),
    ]),
  ],
})
export class CityPairSelectorComponent implements OnInit, ControlValueAccessor {
  private _destroy = inject(DestroyRef);
  private _onChange: (_: any) => void;

  public swapClicked$ = new BehaviorSubject<boolean>(false);
  public swapClicked = false;
  @Input() content: CityPairSelector;
  @Input() hideLegend = false;
  @Input() swappable = true;
  @Input() isMobile = false;
  @Input() separatePartnerCities = false;
  @Input() showValidationErrors = false;
  @Input() customLegendMessage = '';
  @Input() origins: CountryWithAirports[] = [];
  @Input() destinations: CountryWithAirports[] = [];
  @Input() recentAirports: PairedRecentAirportSearches;
  @Input() formWrapper: HTMLFormElement;
  @Input() set origin(origin: Airport) {
    this.formGroup.patchValue({
      origin,
    });
  }

  @Input() set destination(destination: Airport) {
    this.formGroup.patchValue({
      destination,
    });
  }

  @Input() showShortCityName: boolean;
  @Output() originChange: EventEmitter<Airport> = new EventEmitter();
  @Output() userChangedOrigin: EventEmitter<Airport> = new EventEmitter();
  @Output() destinationChange: EventEmitter<Airport> = new EventEmitter();
  @Output() userChangedDestination: EventEmitter<Airport> = new EventEmitter();

  @ViewChildren(CitySelectorComponent)
  citySelectorComponents: QueryList<CitySelectorComponent>;

  readonly CitySelectorType = CitySelectorType;

  readonly formGroup = this.fb.group<CityPair>({
    origin: null,
    destination: null,
  });

  constructor(private fb: FormBuilder) {
    /** Adding debounce time to give the code some time to refresh the state.
     * Without this debounce, if you hit swap many times really fast, one city (From or To) may return undefined at some point.
     */
    this.swapClicked$
      .pipe(takeUntilDestroyed(), debounceTime(25), skip(1))
      .subscribe(value => {
        this.swapClicked = value;
        this.formGroup.patchValue({
          destination: this.formGroup.controls.origin.value,
          origin: this.formGroup.controls.destination.value,
        });
      });
  }

  ngOnInit(): void {
    this.formGroup.valueChanges
      .pipe(skip(1), takeUntilDestroyed(this._destroy))
      .subscribe(({ origin, destination }) => {
        if (this._onChange) {
          // on change available when its registered
          // its only registered when a form control is attached
          // by the parent component. There are cases of this component
          // not having and also having a form control
          this._onChange({ origin, destination });
        }
      });
  }

  focusOnOriginInput() {
    this.citySelectorComponents.first.focusOnInput();
  }

  focusOnDestinationInput() {
    this.citySelectorComponents.last.focusOnInput();
  }

  swap() {
    this.swapClicked$.next(!this.swapClicked);
  }

  registerOnChange(fn: (value: CityPair) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.formGroup.valueChanges
      .pipe(
        filter(val => !!val),
        filter(
          value =>
            (this.formGroup.controls.origin.touched &&
              this.formGroup.controls.destination.touched) ||
            (!!value.origin && !!value.destination),
        ),
        take(1),
      )
      .subscribe(fn);
  }

  validate({ value }: FormControl<CityPair>) {
    return !value?.origin?.code || !value?.destination?.code
      ? {
          originError: this.formGroup.get('origin').errors?.cityInvalid,
          destinationError:
            this.formGroup.get('destination').errors?.cityInvalid,
        }
      : null;
  }

  writeValue(value: CityPair): void {
    // do not update the city pair with an empty value while the form is still untouched
    // this is a workaround for DOT-9843, todo: handle state better
    // this issue happens because during initialization, there are many competing
    // sources of initial state
    if (this.formGroup.untouched && !value.origin) {
      return;
    }
    this.formGroup.patchValue({ ...value });
  }
}
